import React, { useState, useEffect } from 'react'
import { X, Plus } from '@/ui/icons'
import { WaypointResponse } from '@/shared/api/routes'
import { TripResponse, ApiError } from '@/shared/api/types'
import { TripsApi } from '@/shared/api/trips'
import { useAuth } from '@/modules/auth'
import { useTrips } from '@/modules/trips'
import { Button } from '@/ui/components'

interface AddWaypointToTripPanelProps {
  waypoint: WaypointResponse
  onClose: () => void
  onSuccess?: () => void
}

export function AddWaypointToTripPanel({
  waypoint,
  onClose,
  onSuccess
}: AddWaypointToTripPanelProps) {
  const { getAccessToken } = useAuth()
  const { trips, loadTrips, isLoading } = useTrips()
  const [selectedTripId, setSelectedTripId] = useState<string>('')
  const [selectedDay, setSelectedDay] = useState<number | ''>('')
  const [isAdding, setIsAdding] = useState(false)

  const tripsApi = React.useMemo(() => new TripsApi(getAccessToken), [getAccessToken])

  // Ensure trips are loaded when panel opens
  useEffect(() => {
    if (!trips || trips.length === 0) {
      loadTrips().catch((error) => {

      })
    }
  }, [trips, loadTrips])

  // Filter out library trip from available trips
  const availableTrips = trips?.filter(trip => trip.name !== 'My Library') || []

  // Get available days for a trip
  const getTripDays = (trip: TripResponse): number[] => {
    if (!trip.start_date || !trip.end_date) {
      return [1] // Default to day 1 if no dates
    }
    const start = new Date(trip.start_date)
    const end = new Date(trip.end_date)
    const diffTime = end.getTime() - start.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
    return Array.from({ length: diffDays }, (_, i) => i + 1)
  }

  const selectedTrip = availableTrips.find(t => t.id === selectedTripId)
  const availableDays = selectedTrip ? getTripDays(selectedTrip) : []

  const handleAddToTrip = async () => {
    if (!selectedTripId) {

      return
    }

    setIsAdding(true)

    try {
      await tripsApi.addExistingWaypointToTrip(selectedTripId, waypoint.id, {
        trip_day: selectedDay ? (selectedDay as number) : null,
        order_index: 0 // Will be auto-set by backend
      })

      if (onSuccess) {
        onSuccess()
      }
      
      onClose()
    } catch (error) {
      
      // Extract user-friendly error message
      // ApiError already extracts the message from the backend response structure
      // Backend returns: { success: false, error: { message: "...", code: "...", ... } }
      // ApiError extracts error.error.message and sets it as error.message
      let errorMessage = 'Failed to add waypoint to trip'
      if (error instanceof ApiError) {
        errorMessage = error.message
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-earth-50 border border-earth-700 text-foreground rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-earth-700">
          <h3 className="text-lg font-semibold text-foreground">Add Waypoint to Trip</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Waypoint Info */}
          <div className="p-3 bg-earth-100 dark:bg-earth-200 rounded-lg border border-earth-700">
            <div className="text-sm font-medium text-foreground">
              {waypoint.name || 'Unnamed Waypoint'}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {waypoint.latitude?.toFixed(6)}, {waypoint.longitude?.toFixed(6)}
            </div>
          </div>

          {/* Trip Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Trip
            </label>
            {isLoading ? (
              <div className="w-full p-2 text-sm text-muted-foreground text-center border border-earth-700 rounded-md bg-earth-50">
                Loading trips...
              </div>
            ) : availableTrips.length === 0 ? (
              <div className="w-full p-2 text-sm text-muted-foreground text-center border border-earth-700 rounded-md bg-earth-50">
                No trips available. Create a trip first.
              </div>
            ) : (
              <select
                value={selectedTripId}
                onChange={(e) => {
                  setSelectedTripId(e.target.value)
                  setSelectedDay('') // Reset day when trip changes
                }}
                className="w-full p-2 text-sm border border-earth-700 rounded-md bg-earth-50 text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select a trip...</option>
                {availableTrips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    {trip.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Day Selection - Optional */}
          {selectedTripId && availableDays.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Select Day (Optional)
              </label>
              <select
                value={selectedDay}
                onChange={(e) => setSelectedDay(parseInt(e.target.value) || '')}
                className="w-full p-2 text-sm border border-earth-700 rounded-md bg-earth-50 text-foreground focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">No specific day</option>
                {availableDays.map(day => (
                  <option key={day} value={day}>
                    Day {day}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isAdding}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddToTrip}
              disabled={isAdding || !selectedTripId || isLoading}
              className="flex-1"
            >
              {isAdding ? (
                'Adding...'
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Trip
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

